export type PantoneAuthStatus = 'unauthenticated' | 'authenticating' | 'authenticated' | 'error';

export interface PantoneTokenSet {
  accessToken: string;
  refreshToken?: string;
  tokenType: string;
  scope: string[];
  expiresAt: number;
}

export interface PantoneAuthState {
  status: PantoneAuthStatus;
  token: PantoneTokenSet | null;
  error?: string;
  offline: boolean;
  licenseAccepted?: boolean;
  lastRevocationTimestamp?: number;
}

export interface PantoneAuthConfig {
  authorizeUrl: string;
  tokenUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string[];
}

export interface PantoneResourceRequestOptions extends RequestInit {
  path?: string;
}

type Listener = (state: PantoneAuthState) => void;

type TokenResponse = {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  scope?: string;
  expires_in: number;
};

const STORAGE_KEY = 'chromis.pantone.token';
const VERIFIER_SESSION_KEY = 'chromis.pantone.pkce.verifier';
const STATE_SESSION_KEY = 'chromis.pantone.pkce.state';
const LICENSE_ACCEPTANCE_KEY = 'chromis.pantone.license.accepted';
const REVOCATION_LOG_KEY = 'chromis.pantone.revocation.log';

const listeners = new Set<Listener>();

let authState: PantoneAuthState = {
  status: 'unauthenticated',
  token: restoreToken(),
  offline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
  licenseAccepted: restoreLicenseAcceptance(),
  lastRevocationTimestamp: restoreLastRevocationTimestamp(),
};

if (authState.token) {
  authState = normalizeStateWithToken(authState.token);
}

window?.addEventListener?.('online', () => updateState({ offline: false }));
window?.addEventListener?.('offline', () => updateState({ offline: true }));

function restoreToken(): PantoneTokenSet | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PantoneTokenSet;
    if (!parsed.accessToken || !parsed.expiresAt) return null;
    if (Date.now() >= parsed.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('Failed to restore Pantone token:', error);
    return null;
  }
}

function persistToken(token: PantoneTokenSet | null) {
  if (typeof localStorage === 'undefined') return;
  if (!token) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(token));
}

function restoreLicenseAcceptance(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const value = localStorage.getItem(LICENSE_ACCEPTANCE_KEY);
    return value === 'true';
  } catch {
    return false;
  }
}

function persistLicenseAcceptance(accepted: boolean) {
  if (typeof localStorage === 'undefined') return;
  if (accepted) {
    localStorage.setItem(LICENSE_ACCEPTANCE_KEY, 'true');
  } else {
    localStorage.removeItem(LICENSE_ACCEPTANCE_KEY);
  }
}

function restoreLastRevocationTimestamp(): number | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  try {
    const value = localStorage.getItem(REVOCATION_LOG_KEY);
    if (!value) return undefined;
    const parsed = JSON.parse(value);
    return typeof parsed.timestamp === 'number' ? parsed.timestamp : undefined;
  } catch {
    return undefined;
  }
}

function logRevocation(reason: string) {
  if (typeof localStorage === 'undefined') return;
  const timestamp = Date.now();
  const log = {
    timestamp,
    reason,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  };
  try {
    localStorage.setItem(REVOCATION_LOG_KEY, JSON.stringify(log));
  } catch (error) {
    console.warn('Failed to log Pantone access revocation:', error);
  }
}

function normalizeStateWithToken(token: PantoneTokenSet): PantoneAuthState {
  if (Date.now() >= token.expiresAt) {
    persistToken(null);
    return { status: 'unauthenticated', token: null, offline: authState.offline };
  }
  return {
    status: 'authenticated',
    token,
    offline: authState.offline,
  };
}

function updateState(partial: Partial<PantoneAuthState>) {
  authState = {
    ...authState,
    ...partial,
  };
  for (const listener of listeners) {
    listener(authState);
  }
}

function resolveConfig(): PantoneAuthConfig {
  const authorizeUrl = import.meta.env.VITE_PANTONE_AUTHORIZE_URL as string | undefined;
  const tokenUrl = import.meta.env.VITE_PANTONE_TOKEN_URL as string | undefined;
  const clientId = import.meta.env.VITE_PANTONE_CLIENT_ID as string | undefined;
  const redirectUri = import.meta.env.VITE_PANTONE_REDIRECT_URI as string | undefined;
  const scope = (import.meta.env.VITE_PANTONE_SCOPE as string | undefined)?.split(',').map((entry) => entry.trim()).filter(Boolean) ?? ['openid'];

  if (!authorizeUrl || !tokenUrl || !clientId || !redirectUri) {
    throw new Error('Pantone Connect environment variables are missing. Please configure VITE_PANTONE_AUTHORIZE_URL, VITE_PANTONE_TOKEN_URL, VITE_PANTONE_CLIENT_ID, and VITE_PANTONE_REDIRECT_URI.');
  }

  return { authorizeUrl, tokenUrl, clientId, redirectUri, scope } satisfies PantoneAuthConfig;
}

async function createPkceChallenge() {
  const codeVerifier = generateCodeVerifier();
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const codeChallenge = base64UrlEncode(new Uint8Array(digest));

  sessionStorage.setItem(VERIFIER_SESSION_KEY, codeVerifier);
  return { codeVerifier, codeChallenge };
}

function generateCodeVerifier() {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(64);
    crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
  }
  return createRandomId(64);
}

function base64UrlEncode(buffer: Uint8Array) {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function ensureOnline() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Pantone Connect is unavailable offline. Please reconnect to continue.');
  }
}

export function getPantoneAuthState(): PantoneAuthState {
  return authState;
}

export function subscribe(listener: Listener) {
  listeners.add(listener);
  listener(authState);
  return () => {
    listeners.delete(listener);
  };
}

export async function startLogin(): Promise<void> {
  ensureOnline();
  const config = resolveConfig();

  updateState({ status: 'authenticating', error: undefined });

  const { codeChallenge } = await createPkceChallenge();
  const state = createRandomId(32);
  sessionStorage.setItem(STATE_SESSION_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope.join(' '),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  const authorizeTarget = `${config.authorizeUrl}?${params.toString()}`;

  const popup = window.open(
    authorizeTarget,
    'pantone-connect-auth',
    'width=600,height=700,menubar=no,toolbar=no,status=no,resizable=yes',
  );

  if (!popup) {
    window.location.assign(authorizeTarget);
  }
}

export async function completeLogin(url: string): Promise<void> {
  try {
    ensureOnline();
    const config = resolveConfig();

    const parsedUrl = new URL(url);
    const code = parsedUrl.searchParams.get('code') ?? parsedUrl.hash ? new URLSearchParams(parsedUrl.hash.slice(1)).get('code') : null;
    const returnedState = parsedUrl.searchParams.get('state') ?? parsedUrl.hash ? new URLSearchParams(parsedUrl.hash.slice(1)).get('state') : null;

    if (!code) {
      const message = parsedUrl.searchParams.get('error_description') ?? parsedUrl.searchParams.get('error') ?? 'Authorization code not found.';
      throw new Error(message);
    }

    const expectedState = sessionStorage.getItem(STATE_SESSION_KEY);
    if (!expectedState || expectedState !== returnedState) {
      throw new Error('State validation failed. Please retry the Pantone login.');
    }

    const codeVerifier = sessionStorage.getItem(VERIFIER_SESSION_KEY);
    if (!codeVerifier) {
      throw new Error('Verification state missing. Please retry the Pantone login.');
    }

    sessionStorage.removeItem(STATE_SESSION_KEY);
    sessionStorage.removeItem(VERIFIER_SESSION_KEY);

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      code_verifier: codeVerifier,
    });

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    });

    if (!response.ok) {
      const errorPayload = await safeJson(response);
      const message = (errorPayload && (errorPayload.error_description || errorPayload.error)) || 'Pantone token exchange failed.';
      throw new Error(message as string);
    }

    const { access_token, refresh_token, token_type, scope, expires_in } = (await response.json()) as TokenResponse;
    const token: PantoneTokenSet = {
      accessToken: access_token,
      refreshToken: refresh_token,
      tokenType: token_type,
      scope: (scope ?? config.scope.join(' ')).split(/[\s,]+/).filter(Boolean),
      expiresAt: Date.now() + expires_in * 1000,
    };

    persistToken(token);
    updateState(normalizeStateWithToken(token));
  } catch (error) {
    console.error('Pantone Connect login failed:', error);
    persistToken(null);
    updateState({ status: 'error', token: null, error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
}

export function logout(): void {
  persistToken(null);
  updateState({ status: 'unauthenticated', token: null, error: undefined });
}

export function revokeAccess(reason: string = 'user-initiated'): void {
  persistToken(null);
  persistLicenseAcceptance(false);
  logRevocation(reason);
  
  // Clear any cached Pantone data from session/memory
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(VERIFIER_SESSION_KEY);
    sessionStorage.removeItem(STATE_SESSION_KEY);
  }
  
  updateState({
    status: 'unauthenticated',
    token: null,
    error: undefined,
    licenseAccepted: false,
    lastRevocationTimestamp: Date.now(),
  });
}

export function acceptLicense(): void {
  persistLicenseAcceptance(true);
  updateState({ licenseAccepted: true });
}

export function hasAcceptedLicense(): boolean {
  return authState.licenseAccepted === true;
}

export async function refreshTokenIfNeeded(): Promise<boolean> {
  if (!authState.token || !authState.token.refreshToken) {
    return false;
  }

  // Check if token expires within 5 minutes
  const expiresInMs = authState.token.expiresAt - Date.now();
  if (expiresInMs > 5 * 60 * 1000) {
    return true; // Token is still valid
  }

  try {
    ensureOnline();
    const config = resolveConfig();

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: authState.token.refreshToken,
      client_id: config.clientId,
    });

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    });

    if (!response.ok) {
      const errorPayload = await safeJson(response);
      const message = (errorPayload && (errorPayload.error_description || errorPayload.error)) || 'Token refresh failed.';
      console.error('Pantone token refresh failed:', message);
      
      // If refresh fails, revoke access
      if (response.status === 401 || response.status === 403) {
        revokeAccess('token-refresh-failed');
      }
      return false;
    }

    const { access_token, refresh_token, token_type, scope, expires_in } = (await response.json()) as TokenResponse;
    const token: PantoneTokenSet = {
      accessToken: access_token,
      refreshToken: refresh_token ?? authState.token.refreshToken,
      tokenType: token_type,
      scope: (scope ?? config.scope.join(' ')).split(/[\s,]+/).filter(Boolean),
      expiresAt: Date.now() + expires_in * 1000,
    };

    persistToken(token);
    updateState(normalizeStateWithToken(token));
    return true;
  } catch (error) {
    console.error('Pantone token refresh error:', error);
    return false;
  }
}

export function hasValidToken(): boolean {
  return !!authState.token && authState.status === 'authenticated' && Date.now() < authState.token.expiresAt;
}

export function assertAuthenticated(): PantoneTokenSet {
  if (!hasValidToken() || !authState.token) {
    throw new Error('Pantone Connect access is gated. Sign in to continue.');
  }
  return authState.token;
}

export async function fetchPantoneResource(input: string | URL, init?: PantoneResourceRequestOptions) {
  ensureOnline();
  
  // Attempt to refresh token if needed
  await refreshTokenIfNeeded();
  
  const token = assertAuthenticated();

  const request = typeof input === 'string' ? new Request(input, init) : new Request(input, init);
  const headers = new Headers(request.headers);
  headers.set('Authorization', `${token.tokenType ?? 'Bearer'} ${token.accessToken}`);

  const response = await fetch(request, {
    ...init,
    headers,
  });

  // Handle 401/403 responses by revoking access
  if (response.status === 401 || response.status === 403) {
    revokeAccess('api-auth-failed');
    throw new Error('Pantone access has been revoked. Please authenticate again.');
  }

  return response;
}

async function safeJson(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function createRandomId(length: number) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, length);
  }

  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let output = '';
  for (let i = 0; i < length; i++) {
    output += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return output;
}
